import {
  ArrowDown,
  ArrowUp,
  History as HistoryIcon,
  ListMusic,
  Mic2,
  Play,
  Trash2,
  X,
} from 'lucide-react';
import { type ReactElement } from 'react';

import KaraArtwork from '@/components/music/kara-artwork';
import KaraokeLyrics from '@/components/music/karaoke-lyrics';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  useMusicPlayerStore,
  type DrawerTab,
  type HistoryEntry,
  type MiniPlayerTrack,
} from '@/lib/music-player-store';

/* ─── Queue tab ──────────────────────────────────────────────────────── */

function QueueRow({
  track,
  index,
  isActive,
  isFirst,
  isLast,
  onPlay,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  track: MiniPlayerTrack;
  index: number;
  isActive: boolean;
  isFirst: boolean;
  isLast: boolean;
  onPlay: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}): ReactElement {
  return (
    <li
      className={`flex items-center gap-2 rounded-lg border p-2 ${
        isActive
          ? 'border-primary/40 bg-primary/5'
          : 'border-border/60 bg-background/40'
      }`}
    >
      <button
        type="button"
        onClick={onPlay}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
        aria-label={`Skip to ${track.title}`}
      >
        <span className="w-5 shrink-0 text-center text-[11px] font-bold tabular-nums text-muted-foreground">
          {index + 1}
        </span>
        <KaraArtwork
          kind={track.kind}
          animeId={track.animeId}
          animeName={track.animeName}
          className="h-9 w-9"
        />
        <div className="min-w-0 flex-1">
          <p
            className={`line-clamp-1 text-sm font-medium ${
              isActive ? 'text-primary' : 'text-foreground'
            }`}
          >
            {track.title}
          </p>
          <p className="line-clamp-1 text-[11px] text-muted-foreground">
            {track.kind}
            {track.sequence ? ` ${track.sequence}` : ''}
            {track.animeName ? ` · ${track.animeName}` : ''}
          </p>
        </div>
      </button>

      <button
        type="button"
        onClick={onMoveUp}
        disabled={isFirst}
        className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
        aria-label="Move up"
      >
        <ArrowUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={isLast}
        className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
        aria-label="Move down"
      >
        <ArrowDown className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="rounded-md p-1 text-rose-400 transition-colors hover:text-rose-300"
        aria-label="Remove from queue"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

function QueueTab(): ReactElement {
  const queue = useMusicPlayerStore((s) => s.queue);
  const currentIndex = useMusicPlayerStore((s) => s.currentIndex);
  const playFromQueue = useMusicPlayerStore((s) => s.playFromQueue);
  const moveQueueItem = useMusicPlayerStore((s) => s.moveQueueItem);
  const removeQueueItem = useMusicPlayerStore((s) => s.removeQueueItem);
  const clearQueue = useMusicPlayerStore((s) => s.clearQueue);

  if (queue.length === 0) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground">
        <ListMusic className="h-6 w-6 text-muted-foreground/50" />
        <p className="text-sm">Queue is empty.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Up next · {queue.length}
        </p>
        <button
          type="button"
          onClick={clearQueue}
          className="text-[11px] font-medium text-muted-foreground hover:text-destructive"
        >
          Clear all
        </button>
      </div>
      <ol className="space-y-1.5">
        {queue.map((track, index) => (
          <QueueRow
            key={`${track.id}-${index}`}
            track={track}
            index={index}
            isActive={currentIndex === index}
            isFirst={index === 0}
            isLast={index === queue.length - 1}
            onPlay={() => playFromQueue(index)}
            onMoveUp={() => moveQueueItem(index, index - 1)}
            onMoveDown={() => moveQueueItem(index, index + 1)}
            onRemove={() => removeQueueItem(index)}
          />
        ))}
      </ol>
    </div>
  );
}

/* ─── History tab ────────────────────────────────────────────────────── */

function formatRelative(playedAt: number): string {
  const delta = Date.now() - playedAt;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (delta < minute) return 'just now';
  if (delta < hour) return `${Math.floor(delta / minute)}m ago`;
  if (delta < day) return `${Math.floor(delta / hour)}h ago`;
  return `${Math.floor(delta / day)}d ago`;
}

function HistoryRow({
  entry,
  onReplay,
}: {
  entry: HistoryEntry;
  onReplay: () => void;
}): ReactElement {
  return (
    <li className="flex items-center gap-2 rounded-lg border border-border/60 bg-background/40 p-2">
      <KaraArtwork
        kind={entry.kind}
        animeId={entry.animeId}
        animeName={entry.animeName}
        className="h-9 w-9"
      />
      <div className="min-w-0 flex-1">
        <p className="line-clamp-1 text-sm font-medium text-foreground">
          {entry.title}
        </p>
        <p className="line-clamp-1 text-[11px] text-muted-foreground">
          {entry.kind}
          {entry.sequence ? ` ${entry.sequence}` : ''}
          {entry.animeName ? ` · ${entry.animeName}` : ''}
        </p>
        <p className="mt-0.5 text-[10px] text-muted-foreground/70">
          {formatRelative(entry.playedAt)}
        </p>
      </div>
      <button
        type="button"
        onClick={onReplay}
        className="rounded-full bg-primary/10 p-1.5 text-primary transition-colors hover:bg-primary/20"
        aria-label={`Replay ${entry.title}`}
      >
        <Play className="h-3.5 w-3.5 translate-x-[1px] fill-current" />
      </button>
    </li>
  );
}

function HistoryTab(): ReactElement {
  const history = useMusicPlayerStore((s) => s.history);
  const playTrack = useMusicPlayerStore((s) => s.playTrack);
  const clearHistory = useMusicPlayerStore((s) => s.clearHistory);

  if (history.length === 0) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground">
        <HistoryIcon className="h-6 w-6 text-muted-foreground/50" />
        <p className="text-sm">Nothing played yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Recent plays · {history.length}
        </p>
        <button
          type="button"
          onClick={clearHistory}
          className="text-[11px] font-medium text-muted-foreground hover:text-destructive"
        >
          Clear
        </button>
      </div>
      <ol className="space-y-1.5">
        {history.map((entry) => (
          <HistoryRow
            key={`${entry.id}-${entry.playedAt}`}
            entry={entry}
            onReplay={() => {
              // Replay copies the entry into a fresh queue. Strip the
              // `playedAt` field so the queue shape stays compatible.
              const { playedAt: _playedAt, ...track } = entry;
              playTrack(track);
            }}
          />
        ))}
      </ol>
    </div>
  );
}

/* ─── Lyrics tab wrapper ─────────────────────────────────────────────── */

function LyricsTab(): ReactElement {
  const track = useMusicPlayerStore((s) => s.currentTrack);
  if (!track) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        <p className="text-sm">No track playing.</p>
      </div>
    );
  }
  return <KaraokeLyrics track={track} scale="compact" />;
}

/* ─── Drawer shell ───────────────────────────────────────────────────── */

const TABS: Array<{ key: DrawerTab; icon: typeof Mic2; label: string }> = [
  { key: 'lyrics', icon: Mic2, label: 'Lyrics' },
  { key: 'queue', icon: ListMusic, label: 'Queue' },
  { key: 'history', icon: HistoryIcon, label: 'History' },
];

/**
 * Right-side slide-in drawer with three tabs: Lyrics (karaoke-synced),
 * Queue (reorder/remove/skip), History (replay).
 */
export function MusicDrawer(): ReactElement {
  const isDrawerOpen = useMusicPlayerStore((s) => s.isDrawerOpen);
  const drawerTab = useMusicPlayerStore((s) => s.drawerTab);
  const closeDrawer = useMusicPlayerStore((s) => s.closeDrawer);
  const setDrawerTab = useMusicPlayerStore((s) => s.setDrawerTab);

  return (
    <Drawer
      open={isDrawerOpen}
      onOpenChange={(open) => {
        if (!open) closeDrawer();
      }}
      direction="right"
    >
      <DrawerContent className="rounded-l-2xl">
        <DrawerHeader className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
          <DrawerTitle className="text-sm font-semibold">
            {drawerTab === 'lyrics'
              ? 'Lyrics'
              : drawerTab === 'queue'
                ? 'Queue'
                : 'History'}
          </DrawerTitle>
          <button
            type="button"
            onClick={closeDrawer}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" />
          </button>
        </DrawerHeader>

        <div className="flex gap-1 border-b border-border/60 px-3 py-2">
          {TABS.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setDrawerTab(key)}
              aria-pressed={drawerTab === key}
              className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${
                drawerTab === key
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-card/60 hover:text-foreground'
              }`}
            >
              <Icon className="mx-auto mb-0.5 h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2">
          {drawerTab === 'lyrics' && <LyricsTab />}
          {drawerTab === 'queue' && <QueueTab />}
          {drawerTab === 'history' && <HistoryTab />}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default MusicDrawer;
